<template>
  <div>
    <CRow>
      <CCol col="12" xl="12">
        <CAlert show color="danger" v-if="hasWarning">
          {{ hasWarning }}
        </CAlert>
        <CAlert :color="timelineWarningColor" v-if="isScreenTimeline">
          <p v-if="timelineWarning">
            <strong>{{ timelineWarning }}</strong>
          </p>

          <p>The timeline screen must be configured as follows:</p>
          <ul>
            <li>One block containing the 'my_timeline' autofill function</li>
            <li>
              One block containing at least one link. This link is only visible if the user's timeline is empty
            </li>
          </ul>
        </CAlert>
        <CCard>
          <CCardHeader>
            <CDropdown toggler-text="..." class="ml-1 float-right" color="secondary" :caret="false">
              <CDropdownItem>Preview screen</CDropdownItem>
              <CDropdownItem @click="cancelChanges">Cancel changes</CDropdownItem>
              <CDropdownDivider />
              <CDropdownItem :disabled="isNew" @click="deleteScreen">Delete screen</CDropdownItem>
            </CDropdown>

            <CButton :color="saveButton.color" :disabled="saveDisabled" @click="saveData" class="float-right">
              <CIcon :name="saveButton.icon" size="sm" class="mr-1" />{{ saveButton.text }}
            </CButton>
            <h3>
              {{ screen.label || 'New Screen' }}
            </h3>
          </CCardHeader>
          <CCardBody>
            <CRow>
              <CCol sm="4">
                <CInput 
                  label="Screen label" 
                  placeholder="Enter a label for this screen users may see" 
                  description="A descriptive label that can be shown to the user"
                  v-model="screen.label" 
                />
              </CCol>
              <CCol sm="4">
                <CInput 
                  label="Screen name" 
                  placeholder="Enter screen name (not visible to user)" 
                  description="Screen name is not shown to the user"
                  v-model="screen.name" />
              </CCol>
              <CCol sm="4">
                <CSelect
                  label="Icon"
                  :value.sync="screen.asset_name"
                  :options="settings.app_assets"
                  placeholder="Select an icon asset name"
                  description="The icon associated with this screen"
                />
              </CCol>
            </CRow>
            <CRow v-if="!isNew" class="mt-4">
              <CCol>
                <CCardGroup deck>
                  <CCard>
                    <CCardHeader>
                      <strong>Add blocks to screen</strong>
                    </CCardHeader>
                    <CCardBody>
                      <CInput v-model="search" placeholder="Search blocks">
                        <template #prepend-content><CIcon name="cil-magnifying-glass"/></template>
                      </CInput>
                      <CListGroup>
                        <CListGroupItem
                          v-for="(block, index) in allBlocks"
                          :key="index"
                          @click="addBlock(block.id)"
                          :data-idx="index"
                          style="cursor:pointer"
                        >
                          {{ block.name }}
                          <CBadge :color="getBadge(block.block_type)">
                            {{ block.block_type }}
                          </CBadge>
                          <cIcon class="float-right" name="cilPlaylistAdd" />
                        </CListGroupItem>
                      </CListGroup>
                    </CCardBody>
                  </CCard>
                  <CCard>
                    <CCardHeader>
                      <strong>Blocks in {{ screen.label }} screen</strong>
                    </CCardHeader>
                    <CCardBody>
                      <ul
                        class="list-group"
                        v-if="this.hasScreenBlocks"
                        v-sortable="$options.sortOptions"
                        @sorted="moveBlock"
                      >
                        <li
                          v-for="(block, index) in screen.blocks"
                          :key="block.id"
                          :color="itemHighlight(index)"
                          :data-idx="index"
                          class="list-group-item"
                        >
                          <CRow>
                            <CCol sm="10" class="sort-handle">
                              {{ block.name }}
                              <CBadge :color="getBadge(block.block_type)">
                                {{ block.block_type }}
                              </CBadge>
                              <CBadge color="danger" v-if="['featured_podcast', 'featured_episodes'].includes(block.block_type) && index !== 0">
                                This block must be at the top of the screen!
                              </CBadge>
                            </CCol>
                            <CCol sm="2">
                              <CDropdown toggler-text="..." class="float-right item-menu" :caret="false" size="sm">
                                <CDropdownItem @click="removeBlock(block.id)">Remove block</CDropdownItem>
                                <CDropdownDivider />
                                <CDropdownItem @click="goBlock(block.id)">Go to block</CDropdownItem>
                              </CDropdown>
                            </CCol>
                          </CRow>
                        </li>
                      </ul>
                    </CCardBody>
                  </CCard>
                </CCardGroup>
              </CCol>
            </CRow>
          </CCardBody>
          <CCardFooter>
            <span class="float-right"> Screen ID: {{ screen.id }}</span>
          </CCardFooter>
        </CCard>
      </CCol>
    </CRow>
  </div>
</template>

<script>
import { mapGetters, mapState } from 'vuex'
import Sortable from '../../directives/Sortable'

export default {
  name: 'Screen',
  sortOptions: {
    draggable: '.list-group-item',
    handle: '.sort-handle',
  },
  directives: { Sortable },
  data() {
    return {
      isNew: false,
      saveDisabled: true,
      saveButtons: [
        { color: 'secondary', text: 'Saved', icon: 'cil-check-alt' },
        { color: 'danger', text: 'Save Screen', icon: 'cil-save' },
      ],
      fields: [
        { key: 'id' },
        { key: 'label', _classes: 'font-weight-bold' },
        { key: 'name', label: 'Screen name' },
        { key: 'asset_name' },
      ],
      search: '',
      itemHighlightIndex: [],
      settings: this.$settings,
      hasWarning: false
    }
  },
  mounted() {
    this.$store.dispatch('closeWarningsOfType', 'screen'), this.getData()
  },
  watch: {
    screen: {
      deep: true,
      handler(val, oldVal) {
        let found = false
        val.blocks.forEach((bl, index) => {
          if (['featured_podcast', 'featured_episodes'].includes(bl.block_type) && index !== 0 ) {
            this.hasWarning = "The block '"+bl.name+"' may only be used as the first block on the screen"
            this.saveDisabled = true
            found=true
          }
        })
        if (this.hasWarning && found == false) {
          //cleanup old warning
          this.hasWarning = false
        }
        if (this.hasWarning) {
          return
        }

        if (!oldVal.name || !oldVal.label || !oldVal.asset_name) {
          return
        }
        // not same screen
        if (oldVal.id !== val.id) {
          return
        }

        if (this.isScreenTimeline) {
          this.$store.dispatch('checkConfigTimeline')
        }
        // enable save
        this.saveDisabled = false
      },
    },
  },
  destroyed() {
    if (!this.saveDisabled) {
      this.$store.dispatch('issueWarning', {
        id: `screen-${this.screen.id}`,
        text: `You have unsaved changes in screen '${this.screen.label}' (${this.screen.id}).`,
        location: `/screens/${this.screen.id}`,
      })
    }
    this.$store.dispatch('closeError')
  },
  computed: {
    saveButton() {
      return this.saveButtons[this.saveDisabled ? 0 : 1]
    },

    ...mapState({
      screen: state => state.screen.screen,
      allBlocks(state) {
        if (!state.screen.screen.blocks) {
          return state.blocks.blocks
        }

        const block_ids = this.screenBlockIds
        const blks = state.blocks.blocks.filter(val => {
          if (block_ids.includes(val.id)) {
            return false
          } else if (this.search && !val.name.toLowerCase().includes(this.search.toLowerCase())) {
            return false
          } else {
            return true
          }
        })
        return blks
      },
      timelineWarning(state) {
        return state.screen.timelineWarning.warning
      },
      timelineWarningColor(state) {
        return state.screen.timelineWarning.color
      },
    }),

    ...mapGetters(['screenById', 'hasScreenBlocks', 'screenBlockIds', 'isScreenTimeline']),
  },
  methods: {
    getData() {
      const id = this.$route.params.id
      if (id == 0) {
        this.isNew = true
        this.$store.commit('cleanScreen')
        this.$store.dispatch('getBlocks', { $api: this.$api })
        return
      }

      const screen = this.screenById(id)
      if (screen) {
        // we're opening a previousely opened screen
        this.$store.dispatch('checkConfigTimeline')
        this.saveDisabled = false
        return
      }

      this.$store.dispatch('getScreen', { $api: this.$api, id })
      this.saveDisabled = true
    },

    saveData() {
      if (this.isScreenTimeline && this.timelineWarning) {
        this.$store.dispatch('throwError', this.timelineWarning)
        return
      }

      if (this.isNew) {
        this.$store.dispatch('newScreen', { $api: this.$api, $router: this.$router }).then(() => {
          this.isNew = false
          this.saveDisabled = true
        })
      } else {
        this.$store.dispatch('saveScreen', { $api: this.$api, screen: this.screen }).then(() => {
          this.saveDisabled = true
        })
      }
    },

    addBlock(id) {
      this.$store.dispatch('addBlockToScreen', { $api: this.api, id })
      // @TODO: fix this highlight with i (position of item in new block list)
      // this.itemHighlightIndex.push(i)
    },

    removeBlock(id) {
      this.$store.commit('removeScreenBlock', id)
    },

    moveBlock(ev) {
      const { oldIndex, newIndex } = ev
      this.$store.commit('moveScreenBlock', { oldIndex, newIndex })
    },

    deleteScreen() {
      if (
        confirm(
          'This will delete this screen. If the system links to this screen, deleting it will break the app. Are you sure?'
        )
      ) {
        this.$store.dispatch('deleteScreen', {
          $api: this.$api,
          $router: this.$router,
        })
      }
    },

    getBadge(status) {
      switch (status) {
        case 'carousel':
          return 'success'
        case 'list':
          return 'secondary'
        case 'tiles':
          return 'warning'
        case 'links':
          return 'danger'
        case 'featured_podcast':
          return 'info'
        case 'featured_episodes':
          return 'info'
        case 'tile_carousel':
          return 'success'
        default:
          'primary'
      }
    },

    itemHighlight(index) {
      if (this.itemHighlightIndex.includes(index)) {
        return 'success'
      }
      return ''
    },

    cancelChanges() {
      this.$store.commit('cleanScreen')
      this.$router.push({ path: `/screens` })
    },

    goBlock(id) {
      this.$router.push({ path: `/blocks/${id}` })
    },
  },
}
</script>

<style>
.sort-handle {
  cursor: grab;
}
.item-menu {
  background-color: #ebedef;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
}
</style>
